import type { ExecutionProgressHydration } from '../../executionOperations/types';
import { isLoadingStop } from '../../executionOperations/executionHelpers';
import {
  isCompleteOperationalState,
  isInProgressOperationalState,
  isPendingOperationalState,
  normalizeOperationalState,
  type OperationalExecutionState
} from './statusFacade';
import type { ExecutionIncident } from './incidentFacade';
import { hasActiveBlockingIncidents } from './incidentFacade';

export type RouteOperationalStatusKey =
  | 'created'
  | 'loading'
  | 'in_transit'
  | 'unloading'
  | 'ready_for_completion'
  | 'completed'
  | 'problem';

const ROUTE_STATUS_LABELS: Record<RouteOperationalStatusKey, string> = {
  created: 'Создан',
  loading: 'Погрузка',
  in_transit: 'В пути',
  unloading: 'Выгрузка',
  ready_for_completion: 'Готов к завершению',
  completed: 'Завершён',
  problem: 'Проблема в пути'
};

const ROUTE_STATUS_COLORS: Record<RouteOperationalStatusKey, string> = {
  created: 'default',
  loading: 'processing',
  in_transit: 'blue',
  unloading: 'processing',
  ready_for_completion: 'gold',
  completed: 'success',
  problem: 'error'
};

export type RouteOperationalStatus = {
  key: RouteOperationalStatusKey;
  label: string;
  color: string;
  hasFailure: boolean;
  hasBlockingIncident: boolean;
  problemReason: string | null;
  allLoadingComplete: boolean;
  allUnloadingComplete: boolean;
  anyUnloadingStarted: boolean;
};

function stopOperationalState(
  executionState: string,
  type: string
): OperationalExecutionState {
  const base = normalizeOperationalState(executionState);
  if (base === 'failed' || base === 'partially_delivered') return base;
  if (isLoadingStop(type)) {
    if (base === 'unloading_pending' || base === 'unloading_in_progress') return 'loading_pending';
    return base;
  }
  if (base === 'loading_pending' || base === 'loading_in_progress' || base === 'loading_complete') {
    return normalizeOperationalState(executionState);
  }
  return base;
}

export function deriveRouteOperationalStatus(
  hydration: ExecutionProgressHydration | null | undefined,
  incidents: ExecutionIncident[] = [],
  options?: { operationallyFinalized?: boolean }
): RouteOperationalStatus {
  const blocking = hasActiveBlockingIncidents(incidents);
  const blockingReason = blocking
    ? incidents.find((i) => i.status === 'open' && i.blockingFlag)?.comment ??
      'Активный инцидент блокирует исполнение'
    : null;

  if (!hydration || hydration.stops.length === 0) {
    return {
      key: blocking ? 'problem' : 'created',
      label: blocking ? ROUTE_STATUS_LABELS.problem : ROUTE_STATUS_LABELS.created,
      color: blocking ? ROUTE_STATUS_COLORS.problem : ROUTE_STATUS_COLORS.created,
      hasFailure: blocking,
      hasBlockingIncident: blocking,
      problemReason: blockingReason,
      allLoadingComplete: false,
      allUnloadingComplete: false,
      anyUnloadingStarted: false
    };
  }

  const loadingStops = hydration.stops.filter((s) => isLoadingStop(s.type));
  const unloadingStops = hydration.stops.filter((s) => !isLoadingStop(s.type));

  const loadingStates = loadingStops.map((s) => stopOperationalState(s.executionState, s.type));
  const unloadingStates = unloadingStops.map((s) => stopOperationalState(s.executionState, s.type));
  const allStates = [...loadingStates, ...unloadingStates];

  const hasFailure =
    allStates.some((s) => s === 'failed') ||
    hydration.cargo.some((c) => normalizeOperationalState(c.executionState) === 'failed');

  const allLoadingComplete =
    loadingStates.length > 0 &&
    loadingStates.every((s) => s === 'loading_complete' || isCompleteOperationalState(s));
  const allUnloadingComplete =
    unloadingStates.length > 0 &&
    unloadingStates.every((s) => s === 'unloading_complete' || s === 'delivered');
  const anyCargoLoaded = hydration.cargo.some((c) => c.loadedQty > 0);
  const anyUnloadingStarted = unloadingStates.some(
    (s) => s === 'unloading_in_progress' || s === 'unloading_complete' || s === 'delivered'
  );
  const anyLoadingInProgress = loadingStates.some((s) => isInProgressOperationalState(s));
  const allLoadingPending =
    loadingStates.length > 0 && loadingStates.every((s) => isPendingOperationalState(s));

  if (hasFailure || blocking) {
    return {
      key: 'problem',
      label: ROUTE_STATUS_LABELS.problem,
      color: ROUTE_STATUS_COLORS.problem,
      hasFailure: true,
      hasBlockingIncident: blocking,
      problemReason: blockingReason ?? (hasFailure ? 'Ошибка исполнения' : null),
      allLoadingComplete: hydration.progress.allLoadingComplete,
      allUnloadingComplete: hydration.progress.allUnloadingComplete,
      anyUnloadingStarted: unloadingStates.some((s) => !isPendingOperationalState(s))
    };
  }

  const baseFields = {
    hasFailure: false as const,
    hasBlockingIncident: false as const,
    problemReason: null as string | null
  };

  if (allUnloadingComplete && unloadingStops.length > 0) {
    const finalized = options?.operationallyFinalized === true;
    const key: RouteOperationalStatusKey = finalized ? 'completed' : 'ready_for_completion';
    return {
      key,
      label: ROUTE_STATUS_LABELS[key],
      color: ROUTE_STATUS_COLORS[key],
      ...baseFields,
      allLoadingComplete,
      allUnloadingComplete: true,
      anyUnloadingStarted: true
    };
  }

  if (anyUnloadingStarted || unloadingStates.some((s) => isInProgressOperationalState(s))) {
    return {
      key: 'unloading',
      label: ROUTE_STATUS_LABELS.unloading,
      color: ROUTE_STATUS_COLORS.unloading,
      ...baseFields,
      allLoadingComplete,
      allUnloadingComplete: false,
      anyUnloadingStarted: true
    };
  }

  if (
    allLoadingComplete &&
    loadingStops.length > 0 &&
    anyCargoLoaded &&
    !anyUnloadingStarted
  ) {
    return {
      key: 'in_transit',
      label: ROUTE_STATUS_LABELS.in_transit,
      color: ROUTE_STATUS_COLORS.in_transit,
      ...baseFields,
      allLoadingComplete: true,
      allUnloadingComplete: false,
      anyUnloadingStarted: false
    };
  }

  if (anyLoadingInProgress || !allLoadingPending) {
    return {
      key: 'loading',
      label: ROUTE_STATUS_LABELS.loading,
      color: ROUTE_STATUS_COLORS.loading,
      ...baseFields,
      allLoadingComplete,
      allUnloadingComplete: false,
      anyUnloadingStarted: false
    };
  }

  return {
    key: 'created',
    label: ROUTE_STATUS_LABELS.created,
    color: ROUTE_STATUS_COLORS.created,
    ...baseFields,
    allLoadingComplete: false,
    allUnloadingComplete: false,
    anyUnloadingStarted: false
  };
}

export function getRouteOperationalStatusLabel(key: RouteOperationalStatusKey): string {
  return ROUTE_STATUS_LABELS[key];
}

export function getRouteOperationalStatusColor(key: RouteOperationalStatusKey): string {
  return ROUTE_STATUS_COLORS[key];
}
